import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrossClientThreats, generateRuleFromDescription } from '../../hooks/useSecOps';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400',
  high: 'bg-orange-500/20 text-orange-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  low: 'bg-blue-500/20 text-blue-400',
  info: 'bg-zinc-700 text-zinc-400',
};

const IOC_TYPE_COLORS: Record<string, string> = {
  ip: 'bg-purple-500/20 text-purple-400',
  domain: 'bg-cyan-500/20 text-cyan-400',
  hash: 'bg-emerald-500/20 text-emerald-400',
  sha256: 'bg-emerald-500/20 text-emerald-400',
  sha1: 'bg-emerald-500/20 text-emerald-400',
  md5: 'bg-emerald-500/20 text-emerald-400',
  url: 'bg-amber-500/20 text-amber-400',
  email: 'bg-pink-500/20 text-pink-400',
  cidr: 'bg-red-500/20 text-red-400',
  unknown: 'bg-zinc-700 text-zinc-400',
};

type SortField = 'tenant_count' | 'severity' | 'last_seen';

const SEVERITY_ORDER: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? colors['unknown'] ?? 'bg-zinc-700 text-zinc-400'}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: number | string; accent: string }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
      <p className="text-3xl font-bold text-gray-100">{value}</p>
      <p className="text-sm text-gray-400 mt-1">{label}</p>
      <div className={`mt-3 h-1 w-12 rounded ${accent}`} />
    </div>
  );
}

interface GenerateRuleModal {
  threatDescription: string;
  ruleType: 'yara' | 'sigma' | 'detection';
}

export default function CrossClientThreatsPage() {
  const navigate = useNavigate();
  const { data, loading, error } = useCrossClientThreats();
  const [sortField, setSortField] = useState<SortField>('tenant_count');
  const [ruleModal, setRuleModal] = useState<GenerateRuleModal | null>(null);
  const [generatingRule, setGeneratingRule] = useState(false);
  const [generatedRule, setGeneratedRule] = useState<{ rule: string; description: string } | null>(null);
  const [ruleError, setRuleError] = useState<string | null>(null);

  async function handleGenerateRule() {
    if (!ruleModal) return;
    setGeneratingRule(true);
    setRuleError(null);
    setGeneratedRule(null);
    try {
      const result = await generateRuleFromDescription(ruleModal.threatDescription, ruleModal.ruleType);
      setGeneratedRule({ rule: result.rule, description: result.description });
    } catch (err) {
      setRuleError(err instanceof Error ? err.message : 'Rule generation failed');
    } finally {
      setGeneratingRule(false);
    }
  }

  const dashboard = data?.data;
  const summary = dashboard?.summary;
  const platformThreats = dashboard?.platform_threats ?? [];
  const localMatches = dashboard?.local_matches ?? [];
  const corrIncidents = dashboard?.correlation_incidents ?? [];

  const sortedThreats = [...platformThreats].sort((a, b) => {
    if (sortField === 'tenant_count') return b.tenant_count - a.tenant_count;
    if (sortField === 'severity') return (SEVERITY_ORDER[b.severity] ?? 0) - (SEVERITY_ORDER[a.severity] ?? 0);
    if (sortField === 'last_seen') return (b.last_seen || '').localeCompare(a.last_seen || '');
    return 0;
  });

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-zinc-800 rounded w-72" />
          <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-800 rounded-lg" />)}</div>
          <div className="h-96 bg-zinc-800 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
        <h1 className="text-2xl font-bold text-gray-100 mb-6">Cross-Client Threat Intelligence</h1>
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-sm text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Cross-Client Threat Intelligence</h1>
          <p className="text-sm text-gray-500 mt-1">IOC propagation across tenants via Collective sharing</p>
        </div>
        <button
          onClick={() => navigate('/secops')}
          className="px-3 py-2 text-sm bg-zinc-800 border border-zinc-700 text-gray-300 rounded-md hover:bg-zinc-700"
        >
          Command Center
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Platform Threats" value={summary?.total_platform_threats ?? 0} accent="bg-indigo-500" />
        <SummaryCard label="Detected in This Tenant" value={summary?.threats_in_this_tenant ?? 0} accent="bg-red-500" />
        <SummaryCard label="Affected Organizations" value={summary?.total_affected_orgs ?? 0} accent="bg-amber-500" />
        <SummaryCard label="CORR-008 Incidents" value={summary?.corr_008_incidents ?? 0} accent="bg-purple-500" />
      </div>

      {/* Platform Threats Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-100">Platform Threats</h2>
            <p className="text-xs text-gray-500 mt-0.5">IOCs detected across multiple tenants via Collective</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Sort:</span>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value as SortField)}
              className="text-xs bg-zinc-800 border border-zinc-700 text-gray-300 rounded px-2 py-1"
            >
              <option value="tenant_count">Tenant Count</option>
              <option value="severity">Severity</option>
              <option value="last_seen">Last Seen</option>
            </select>
          </div>
        </div>
        {sortedThreats.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            No cross-client threats detected. IOCs are shared anonymously through the Collective platform.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-zinc-800/50 border-b border-zinc-800">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">IOC</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Type</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Severity</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Tenants</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">MITRE</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Last Seen</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Detected Here</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {sortedThreats.map((threat) => (
                  <tr key={threat.ioc_value} className="hover:bg-zinc-800/50">
                    <td className="px-6 py-3">
                      <p className="text-sm font-mono text-gray-200 truncate max-w-[200px]" title={threat.ioc_value}>
                        {threat.ioc_value}
                      </p>
                      {threat.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {threat.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">{tag}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><Badge value={threat.ioc_type} colors={IOC_TYPE_COLORS} /></td>
                    <td className="px-4 py-3"><Badge value={threat.severity} colors={SEVERITY_COLORS} /></td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-200">{threat.tenant_count}</span>
                      <span className="text-xs text-gray-500 ml-1">orgs</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {threat.mitre_techniques.slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded font-mono">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {threat.last_seen ? new Date(threat.last_seen).toLocaleDateString() : '--'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {threat.detected_in_this_tenant ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-400">
                          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setRuleModal({
                            threatDescription: [
                              `IOC: ${threat.ioc_value} (${threat.ioc_type})`,
                              `Severity: ${threat.severity}`,
                              threat.mitre_techniques.length > 0 ? `MITRE: ${threat.mitre_techniques.join(', ')}` : null,
                              threat.tags.length > 0 ? `Tags: ${threat.tags.join(', ')}` : null,
                            ].filter(Boolean).join('\n'),
                            ruleType: 'yara',
                          });
                          setGeneratedRule(null);
                          setRuleError(null);
                        }}
                        className="text-xs px-2 py-1 rounded border border-purple-700 text-purple-400 hover:bg-purple-900/20 whitespace-nowrap"
                      >
                        Generate Rule
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Local Matches */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-800">
          <h2 className="text-base font-semibold text-gray-100">Local Alert Matches</h2>
          <p className="text-xs text-gray-500 mt-0.5">Alerts in this tenant that match platform-wide IOCs</p>
        </div>
        {localMatches.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            No local alerts match current platform IOCs — this tenant is clear of known cross-client threats.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-zinc-800/50 border-b border-zinc-800">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Alert</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Severity</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">IOC Matched</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Detected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {localMatches.map((match) => (
                  <tr
                    key={`${match.alert_id}-${match.ioc_matched}`}
                    className="hover:bg-zinc-800/50 cursor-pointer"
                    onClick={() => navigate(`/secops/alerts/${match.alert_id}`)}
                  >
                    <td className="px-6 py-3">
                      <p className="text-sm text-gray-200 truncate max-w-[300px]">{match.alert_title}</p>
                      <p className="text-[10px] text-gray-500 font-mono">{match.alert_id}</p>
                    </td>
                    <td className="px-4 py-3"><Badge value={match.severity} colors={SEVERITY_COLORS} /></td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-300">{match.ioc_matched}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {new Date(match.detected_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CORR-008 Correlation Incidents */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-800">
          <h2 className="text-base font-semibold text-gray-100">CORR-008 Cross-Tenant Incidents</h2>
          <p className="text-xs text-gray-500 mt-0.5">Correlation incidents from cross-tenant threat propagation rule</p>
        </div>
        {corrIncidents.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            No CORR-008 incidents — no cross-tenant threat propagation detected.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-zinc-800/50 border-b border-zinc-800">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Incident</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Severity</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Confidence</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Affected Assets</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {corrIncidents.map((inc) => (
                  <tr
                    key={inc.incident_id}
                    className="hover:bg-zinc-800/50 cursor-pointer"
                    onClick={() => navigate(`/secops/incidents/${inc.incident_id}`)}
                  >
                    <td className="px-6 py-3">
                      <p className="text-sm text-gray-200 truncate max-w-[300px]">{inc.title}</p>
                      <p className="text-[10px] text-gray-500 font-mono">{inc.incident_id}</p>
                    </td>
                    <td className="px-4 py-3"><Badge value={inc.severity} colors={SEVERITY_COLORS} /></td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        inc.confidence >= 0.8 ? 'text-red-400 bg-red-500/20' :
                        inc.confidence >= 0.6 ? 'text-orange-400 bg-orange-500/20' :
                        'text-yellow-400 bg-yellow-500/20'
                      }`}>
                        {Math.round(inc.confidence * 100)}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {inc.affected_assets.slice(0, 3).map((asset) => (
                          <span key={asset} className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">{asset}</span>
                        ))}
                        {inc.affected_assets.length > 3 && (
                          <span className="text-[10px] text-zinc-500">+{inc.affected_assets.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {new Date(inc.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate Detection Rule Modal */}
      {ruleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setRuleModal(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-100 mb-1">Generate Detection Rule</h3>
            <p className="text-xs text-gray-500 mb-4">ARGUS FORGE will generate a detection rule for this threat.</p>

            <div className="mb-4">
              <p className="text-xs font-medium text-gray-400 mb-1">Threat Description</p>
              <pre className="text-xs font-mono text-gray-300 bg-zinc-800 rounded p-3 whitespace-pre-wrap">{ruleModal.threatDescription}</pre>
            </div>

            <div className="mb-4">
              <p className="text-xs font-medium text-gray-400 mb-2">Rule Type</p>
              <div className="flex gap-2">
                {(['yara', 'sigma', 'detection'] as const).map(rt => (
                  <button
                    key={rt}
                    onClick={() => setRuleModal(m => m ? { ...m, ruleType: rt } : null)}
                    className={`text-xs px-3 py-1.5 rounded border uppercase font-mono ${
                      ruleModal.ruleType === rt
                        ? 'border-purple-500 bg-purple-900/30 text-purple-300'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                  >
                    {rt}
                  </button>
                ))}
              </div>
            </div>

            {ruleError && (
              <div className="mb-4 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded p-3">{ruleError}</div>
            )}

            {generatedRule && (
              <div className="mb-4 space-y-2">
                {generatedRule.description && (
                  <p className="text-xs text-zinc-400">{generatedRule.description}</p>
                )}
                <div className="relative">
                  <pre className="text-xs font-mono text-green-300 bg-zinc-950 border border-zinc-700 rounded p-4 overflow-x-auto whitespace-pre">{generatedRule.rule}</pre>
                  <button
                    onClick={() => navigator.clipboard?.writeText(generatedRule.rule)}
                    className="absolute top-2 right-2 text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded hover:text-zinc-200"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 border-t border-zinc-800 pt-4">
              <button onClick={() => setRuleModal(null)} className="text-sm text-zinc-500 hover:text-zinc-300 px-3 py-1.5">Close</button>
              <button
                onClick={handleGenerateRule}
                disabled={generatingRule}
                className="text-sm px-4 py-1.5 bg-purple-700 hover:bg-purple-600 text-white rounded disabled:opacity-50"
              >
                {generatingRule ? 'Generating…' : generatedRule ? 'Regenerate' : 'Generate Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
